class LeadFormModal extends HTMLElement {
  constructor() {
    super();

    this.minFillMs = 3000;
    this.maxAttempts = 3;
    this.retryBackoffMs = 800;
    this.openedBy = null;
    this.hashTriggersBound = false;

    this.form = this.querySelector('[data-lead-form]');
    this.submitBtn = this.querySelector('[data-submit-btn]');
    this.successPanel = this.querySelector('[data-success-panel]');
    this.successClose = this.querySelector('[data-success-close]');
    this.closeBtn = this.querySelector('[id^="ModalClose-"]');
    this.overlay = this.querySelector('.lead-form-modal__overlay');

    this.closeBtn.addEventListener('click', this.hide.bind(this));
    this.overlay.addEventListener('click', this.hide.bind(this));
    if (this.successClose) this.successClose.addEventListener('click', this.hide.bind(this));

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
    this.bindHashTriggers();
  }

  isTriggerHash(hash) {
    const normalized = (hash || '').toLowerCase();
    return normalized === '#request' || normalized === '#leadformmodal';
  }

  formLocationForHash(hash) {
    return (hash || '').toLowerCase() === '#request' ? 'hash_request' : 'hash_leadform_modal';
  }

  createHashOpener(hash) {
    const location = this.formLocationForHash(hash);
    return {
      _fromHash: true,
      getAttribute: (name) => (name === 'data-form-location' ? location : null),
      focus: () => {}
    };
  }

  clearTriggerHash() {
    if (!this.isTriggerHash(window.location.hash)) return;
    history.replaceState({}, '', window.location.pathname + window.location.search);
  }

  bindHashTriggers() {
    if (this.hashTriggersBound) return;
    this.hashTriggersBound = true;

    this.onHashChange = () => {
      const hash = window.location.hash;
      if (this.isTriggerHash(hash)) {
        this.show(this.createHashOpener(hash));
        return;
      }

      if (this.hasAttribute('open') && this.openedBy && this.openedBy._fromHash) {
        this.hide({ preserveHash: true });
      }
    };

    window.addEventListener('hashchange', this.onHashChange);
    this.onHashChange();
  }

  show(opener) {
    this.openedBy = opener;
    this.resetState();
    this.populateMetadata(opener);
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');

    const firstInput = this.form.querySelector('input:not([type=hidden]), select');
    if (typeof trapFocus === 'function') {
      trapFocus(this, firstInput || this.querySelector('[role="document"]'));
    } else if (firstInput) {
      firstInput.focus();
    }
  }

  hide(options) {
    const opts = options || {};
    if (!opts.preserveHash) this.clearTriggerHash();

    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');
    this.removeAttribute('data-state');

    this.form.reset();
    this.submitBtn.removeAttribute('data-loading');
    this.submitBtn.disabled = false;
    this.clearValidationErrors();

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.openedBy);
    } else if (this.openedBy) {
      this.openedBy.focus();
    }

    document.body.dispatchEvent(new CustomEvent('modalClosed'));
  }

  resetState() {
    this.form.reset();
    this.removeAttribute('data-state');
    this.submitBtn.removeAttribute('data-loading');
    this.submitBtn.disabled = false;
    this.clearValidationErrors();
  }

  clearValidationErrors() {
    this.querySelectorAll('[data-field-invalid]').forEach((el) => {
      el.removeAttribute('data-field-invalid');
    });
    this.form.querySelectorAll('[aria-invalid]').forEach((el) => {
      el.removeAttribute('aria-invalid');
    });
  }

  // Ad params are captured site-wide into localStorage['euas_ad_params'] on the
  // first landing by the tracking script in sections/euas-quote-request.liquid.
  // That capture only writes keys when a value is actually present, so the
  // original values survive later page loads that have no query string.
  storedAdParams() {
    try {
      return JSON.parse(localStorage.getItem('euas_ad_params') || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  populateMetadata(trigger) {
    const params = new URLSearchParams(window.location.search);
    const stored = this.storedAdParams();
    const set = (name, value) => {
      const el = this.form.querySelector(`input[type="hidden"][name="${name}"]`);
      if (el) el.value = value || '';
    };

    // Live URL wins; fall back to the stored value. Without the fallback,
    // attribution is lost whenever a CTA navigates to a URL that carries no
    // query string (several CTAs are hardcoded absolute URLs), or whenever the
    // visitor browses to another page before submitting. This mirrors how the
    // legacy Pipedrive form resolves the same values.
    const resolve = (key) => params.get(key) || stored[key] || '';

    set('utm_source', resolve('utm_source'));
    set('utm_medium', resolve('utm_medium'));
    set('utm_campaign', resolve('utm_campaign'));
    set('utm_term', resolve('utm_term'));
    set('utm_content', resolve('utm_content'));
    // Google click id, used to match the deal back to the ad click in Pipedrive.
    // Already captured into euas_ad_params, so resolve() picks it up even when
    // the query string has been dropped from the URL.
    set('gclid', resolve('gclid'));
    set('page_url', window.location.href);
    set('form_location', trigger ? trigger.getAttribute('data-form-location') : 'unspecified');
    set('form_load_timestamp', String(Date.now()));
  }

  validate(values) {
    const fields = ['full_name', 'company', 'email', 'phone'];
    let firstInvalid = null;

    fields.forEach((name) => {
      const wrapper = this.form.querySelector(`[data-field="${name}"]`);
      const input = this.form.querySelector(`[name="${name}"]`);
      if (wrapper) wrapper.removeAttribute('data-field-invalid');
      if (input) input.removeAttribute('aria-invalid');
    });

    const fail = (name) => {
      const wrapper = this.form.querySelector(`[data-field="${name}"]`);
      const input = this.form.querySelector(`[name="${name}"]`);
      if (wrapper) wrapper.setAttribute('data-field-invalid', '');
      if (input) input.setAttribute('aria-invalid', 'true');
      if (!firstInvalid) firstInvalid = name;
    };

    if (!values.full_name || values.full_name.trim().length < 2) fail('full_name');
    if (!values.company || values.company.trim().length === 0) fail('company');
    if (!values.email || !/^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(values.email.trim())) fail('email');
    if (!values.phone || (values.phone.replace(/\D+/g, '')).length < 10) fail('phone');

    if (firstInvalid) {
      const el = this.form.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
      return false;
    }
    return true;
  }

  readForm() {
    const fd = new FormData(this.form);
    const out = {};
    fd.forEach((v, k) => { out[k] = typeof v === 'string' ? v : ''; });
    out.submitted_at = new Date().toISOString();
    return out;
  }

  showSuccess() {
    this.setAttribute('data-state', 'success');
    this.submitBtn.removeAttribute('data-loading');
    this.submitBtn.disabled = false;
    if (this.successClose) this.successClose.focus();
  }

  showError() {
    this.setAttribute('data-state', 'error');
    this.submitBtn.removeAttribute('data-loading');
    this.submitBtn.disabled = false;
  }

  fakeSuccess() {
    this.showSuccess();
  }

  pushDataLayerEvent(values, dealId) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = {
  event: 'lead_form_submission',
  form_location: values.form_location || 'unspecified',
  industry: values.industry || '',
  utm_source: values.utm_source || '',
  utm_medium: values.utm_medium || '',
  utm_campaign: values.utm_campaign || '',
  utm_term: values.utm_term || '',
  utm_content: values.utm_content || ''
};
      if (dealId) payload.deal_id = String(dealId);
      window.dataLayer.push(payload);
    } catch (e) { /* analytics is non-blocking */ }
  }

  resolveWebhookUrl(raw) {
    const trimmed = (raw || '').trim();
    if (!trimmed) return { ok: false, reason: 'empty' };

    let parsed;
    try {
      parsed = new URL(trimmed, window.location.origin);
    } catch (e) {
      return { ok: false, reason: 'invalid' };
    }

    if (!/^https?:$/i.test(parsed.protocol)) {
      return { ok: false, reason: 'invalid-protocol' };
    }

    // Missing https:// makes the browser treat the value as a path on the store domain.
    if (parsed.origin === window.location.origin) {
      return { ok: false, reason: 'relative-to-store', resolved: parsed.href };
    }

    return { ok: true, url: parsed.href, host: parsed.hostname };
  }

  postWithRetry(url, payload, attempt) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(async (res) => {
      const rawText = await res.text();
      let data = {};
      try { data = rawText ? JSON.parse(rawText) : {}; } catch (e) { data = { _parseError: true, _rawPreview: rawText.slice(0, 200) }; }
      if (res.ok) return data;
      const err = new Error('HTTP ' + res.status);
      err.httpStatus = res.status;
      throw err;
    }).catch((err) => {
      const shouldRetry = attempt < this.maxAttempts && (!err.httpStatus || err.httpStatus >= 500);
      if (shouldRetry) {
        return new Promise((resolve) => {
          setTimeout(resolve, this.retryBackoffMs * attempt);
        }).then(() => this.postWithRetry(url, payload, attempt + 1));
      }
      throw err;
    });
  }

  handleSubmit(event) {
    event.preventDefault();
    const values = this.readForm();

    if (!this.validate(values)) return;

    if (values.company_website && values.company_website.trim() !== '') {
      this.fakeSuccess();
      return;
    }

    const openedAt = parseInt(values.form_load_timestamp || '0', 10);
    if (!openedAt || (Date.now() - openedAt) < this.minFillMs) {
      this.fakeSuccess();
      return;
    }

    const webhookRaw = this.getAttribute('data-webhook-url') || '';
    const webhookCheck = this.resolveWebhookUrl(webhookRaw);
    if (!webhookCheck.ok) {
      console.error('[lead-form] Invalid webhook URL in theme settings:', webhookCheck.reason, webhookRaw);
      this.showError();
      return;
    }

    const webhook = webhookCheck.url;

    delete values.company_website;
    delete values.form_load_timestamp;

    this.submitBtn.setAttribute('data-loading', '');
    this.submitBtn.disabled = true;
    this.removeAttribute('data-state');

    this.postWithRetry(webhook, values, 1)
      .then((data) => {
        this.pushDataLayerEvent(values, data && data.deal_id);
        this.showSuccess();
      })
      .catch((err) => {
        console.error('[lead-form] Submission failed after retries:', err);
        this.showError();
      });
  }
}

customElements.define('lead-form-modal', LeadFormModal);
